import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

function fmt(v) {
    if (v == null) return "—";
    const n = v / 1_000_000;
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(2)} mld`;
    return `${n.toFixed(1)} M`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
    } catch { return iso; }
}

const CAT_COLOR = {
    acquisto: "rgb(var(--tc-ff3b30))",
    vendita: "rgb(var(--tc-2bd96b))",
    prestito_in: "rgb(var(--tc-ffb020))",
    prestito_out: "rgb(var(--tc-2bb6ff))",
    svincolo: "rgb(var(--tc-ff7a1a))",
    acquisto_svincolato: "rgb(var(--tc-b98bff))",
};

function TeamDetailModal({ teamId, season, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        setLoading(true); setErr("");
        api.get(`/admin/market-stats/team/${teamId}`, { params: { season } })
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    }, [teamId, season]);

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 overflow-y-auto"
            onClick={onClose}
            data-testid="market-stats-detail-modal"
        >
            <div
                className="op-card w-full max-w-3xl mt-10 mb-10 p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <div className="op-label text-t2bb6ff">// DETTAGLIO OPERAZIONI</div>
                        <h3 className="op-heading text-2xl mt-1" data-testid="detail-team-name">
                            {data?.team_name || "…"}
                        </h3>
                        <div className="op-mono text-[11px] text-t8f95a3 mt-1">
                            {season === "all" ? "Tutte le stagioni" : `Stagione ${season}`}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="op-mono text-xs px-3 py-1.5 border border-t22252a text-t8f95a3 hover:text-tffffff hover:border-tffffff transition-colors"
                        data-testid="detail-close-btn"
                    >
                        ✕ Chiudi
                    </button>
                </div>

                {err && <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2 mb-4">{err}</div>}
                {loading && <div className="op-mono text-xs text-t8f95a3 py-6 text-center">Caricamento…</div>}

                {data && !loading && (
                    <div className="op-card overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-[10px] uppercase tracking-wider text-t8f95a3 border-b border-t22252a">
                                <tr>
                                    <th className="py-2 px-3 text-left">Data</th>
                                    <th className="py-2 px-3 text-left">Stag.</th>
                                    <th className="py-2 px-3 text-left">Operazione</th>
                                    <th className="py-2 px-3 text-left">Giocatore</th>
                                    <th className="py-2 px-3 text-left">Controparte</th>
                                    <th className="py-2 px-3 text-right">Importo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.operations.map((o, i) => (
                                    <tr key={i} className="border-b border-t1a1d24 hover:bg-t0f1115" data-testid={`detail-op-${i}`}>
                                        <td className="py-2 px-3 op-mono text-[11px] text-t8f95a3">{fmtDate(o.date)}</td>
                                        <td className="py-2 px-3 op-mono text-[11px] text-t8f95a3">{o.season}</td>
                                        <td className="py-2 px-3">
                                            <span className="op-mono text-[11px] font-bold" style={{ color: CAT_COLOR[o.category] || "rgb(var(--tc-ffffff))" }}>
                                                {o.label}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 text-tffffff">{o.player || "—"}</td>
                                        <td className="py-2 px-3 text-t8f95a3">{o.counterpart || "—"}</td>
                                        <td className="py-2 px-3 text-right op-mono" style={{ color: o.direction === "in" ? "rgb(var(--tc-2bd96b))" : "rgb(var(--tc-ff3b30))" }}>
                                            {o.amount ? `${o.direction === "in" ? "+" : "−"}${fmt(o.amount)}` : "—"}
                                        </td>
                                    </tr>
                                ))}
                                {data.operations.length === 0 && (
                                    <tr><td colSpan={6} className="py-6 text-center op-mono text-xs text-t8f95a3" data-testid="detail-empty">Nessuna operazione in questo periodo.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AdminStatisticheMercato() {
    const [season, setSeason] = useState("all");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [openTeam, setOpenTeam] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const { data } = await api.get("/admin/market-stats", { params: { season } });
            setData(data);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, [season]);

    useEffect(() => { load(); }, [load]);

    const rows = data?.rows || [];
    const totals = data?.totals;

    return (
        <div data-testid="admin-market-stats-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-b98bff))" }}>// STATISTICHE MERCATO</div>
            <h2 className="op-heading text-3xl mt-2 mb-2">Statistiche operazioni</h2>
            <p className="op-mono text-xs text-t8f95a3 mb-5 max-w-3xl leading-relaxed">
                Riepilogo per squadra di <strong className="text-tffffff">acquisti, vendite, prestiti, svincoli</strong> e
                {" "}<strong className="text-tffffff">acquisti dagli svincolati</strong>. Ricostruito dai dati esistenti.
                Clicca una riga per il dettaglio operazione per operazione.
            </p>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <label className="op-mono text-xs text-t8f95a3 flex items-center gap-2">
                    Stagione
                    <select
                        value={season}
                        onChange={(e) => setSeason(e.target.value)}
                        className="bg-t0f1115 border border-t22252a text-tffffff op-mono text-xs px-3 py-2 focus:border-tb98bff outline-none"
                        data-testid="market-stats-season-select"
                    >
                        <option value="all">Totale (tutte)</option>
                        {(data?.seasons || []).map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </label>
                <button
                    onClick={load}
                    disabled={loading}
                    className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-tb98bff text-tb98bff hover:bg-tb98bff/10 transition-colors disabled:opacity-40"
                    data-testid="market-stats-refresh"
                >
                    {loading ? "Analisi…" : "↻ Aggiorna"}
                </button>
            </div>

            {err && <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2 mb-4" data-testid="market-stats-err">{err}</div>}

            <div className="op-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-t8f95a3 border-b border-t22252a">
                        <tr>
                            <th className="py-2 px-3 text-left">Squadra</th>
                            <th className="py-2 px-3 text-right" title="Acquisti da altre squadre">Acq.</th>
                            <th className="py-2 px-3 text-right">Spesa</th>
                            <th className="py-2 px-3 text-right" title="Vendite ad altre squadre">Vend.</th>
                            <th className="py-2 px-3 text-right">Incassi</th>
                            <th className="py-2 px-3 text-right" title="Prestiti presi / dati">Prest. (in/out)</th>
                            <th className="py-2 px-3 text-right" title="Svincoli effettuati">Svinc.</th>
                            <th className="py-2 px-3 text-right" title="Presi dagli svincolati (aste)">Da svinc.</th>
                            <th className="py-2 px-3 text-right" title="Incassi − spese totali">Saldo netto</th>
                            <th className="py-2 px-3 text-right">Tot op.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr
                                key={r.team_id}
                                className="border-b border-t1a1d24 hover:bg-t0f1115 cursor-pointer"
                                onClick={() => setOpenTeam(r.team_id)}
                                data-testid={`market-stats-row-${r.team_id}`}
                            >
                                <td className="py-2 px-3 text-tffffff flex items-center gap-2">
                                    {r.logo_url && <img src={r.logo_url} alt="" className="w-5 h-5 object-contain" />}
                                    {r.team_name || "—"}
                                </td>
                                <td className="py-2 px-3 text-right op-mono text-tffffff">{r.acquisti_count || "—"}</td>
                                <td className="py-2 px-3 text-right op-mono text-tff3b30">{r.spesa_totale ? fmt(r.spesa_totale) : "—"}</td>
                                <td className="py-2 px-3 text-right op-mono text-tffffff">{r.vendite_count || "—"}</td>
                                <td className="py-2 px-3 text-right op-mono text-t2bd96b">{r.incassi_totale ? fmt(r.incassi_totale) : "—"}</td>
                                <td className="py-2 px-3 text-right op-mono text-t8f95a3">
                                    <span className="text-tffb020">{r.prestiti_in_count}</span> / <span className="text-t2bb6ff">{r.prestiti_out_count}</span>
                                </td>
                                <td className="py-2 px-3 text-right op-mono text-tff7a1a">{r.svincoli_count || "—"}</td>
                                <td className="py-2 px-3 text-right op-mono text-tb98bff">{r.presi_svincolati_count || "—"}</td>
                                <td className="py-2 px-3 text-right op-mono font-bold" style={{ color: r.saldo_netto >= 0 ? "rgb(var(--tc-2bd96b))" : "rgb(var(--tc-ff3b30))" }} data-testid={`market-stats-saldo-${r.team_id}`}>
                                    {r.saldo_netto === 0 ? "0" : `${r.saldo_netto > 0 ? "+" : "−"}${fmt(Math.abs(r.saldo_netto))}`}
                                </td>
                                <td className="py-2 px-3 text-right op-mono text-tffffff font-bold">{r.operazioni_totali || 0}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && !loading && (
                            <tr><td colSpan={10} className="py-6 text-center op-mono text-xs text-t8f95a3" data-testid="market-stats-empty">Nessuna operazione registrata.</td></tr>
                        )}
                    </tbody>
                    {totals && rows.length > 0 && (
                        <tfoot className="border-t-2 border-t22252a">
                            <tr className="op-mono text-xs" data-testid="market-stats-totals">
                                <td className="py-2 px-3 text-t8f95a3 uppercase tracking-wider">Totale lega</td>
                                <td className="py-2 px-3 text-right text-tffffff">{totals.acquisti_count}</td>
                                <td className="py-2 px-3 text-right text-tff3b30">{fmt(totals.spesa_totale)}</td>
                                <td className="py-2 px-3 text-right text-tffffff">{totals.vendite_count}</td>
                                <td className="py-2 px-3 text-right text-t2bd96b">{fmt(totals.incassi_totale)}</td>
                                <td className="py-2 px-3 text-right text-t8f95a3">{totals.prestiti_in_count} / {totals.prestiti_out_count}</td>
                                <td className="py-2 px-3 text-right text-tff7a1a">{totals.svincoli_count}</td>
                                <td className="py-2 px-3 text-right text-tb98bff">{totals.presi_svincolati_count}</td>
                                <td className="py-2 px-3 text-right"></td>
                                <td className="py-2 px-3 text-right text-tffffff font-bold">{totals.operazioni_totali}</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {openTeam && (
                <TeamDetailModal teamId={openTeam} season={season} onClose={() => setOpenTeam(null)} />
            )}
        </div>
    );
}
